import React from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  TextField,
  Typography,
} from "@mui/material";
import { useAuth } from "../../../common/auth";
import { C } from "../../../common/theme";
import {
  RefundPreview,
  RefundResult,
  SalesOrder,
  getRefundPreview,
  refundOrder,
} from "../../../common/requests/sales/endpoints";
import { Banner, ErrorState, LoadingRows, useIsNarrow } from "../../../ui";
import { formatMoney } from "../salesData";

interface CancelOrderDialogProps {
  order: SalesOrder | null;
  onClose: () => void;
  onRefunded: (result: RefundResult) => void;
}

const errorText = (err: any, fallback: string) => {
  const detail = err?.response?.data?.detail;
  return typeof detail === "string" ? detail : detail?.message ?? fallback;
};

/** Cancels a whole paid order: items become Refunded and the sellers'
 * earnings for it are removed. Shows exactly what will change first (from
 * the backend's refund preview). Doesn't refund the buyer's card. */
export default function CancelOrderDialog({ order, onClose, onRefunded }: CancelOrderDialogProps) {
  const { idToken } = useAuth();
  const [preview, setPreview] = React.useState<RefundPreview | null>(null);
  const [loadError, setLoadError] = React.useState<string | null>(null);
  const [reason, setReason] = React.useState("");
  const [deductWithdrawn, setDeductWithdrawn] = React.useState(false);
  const [busy, setBusy] = React.useState(false);
  const [submitError, setSubmitError] = React.useState<string | null>(null);
  const narrow = useIsNarrow(600);

  React.useEffect(() => {
    if (!order || !idToken) return;
    setPreview(null);
    setLoadError(null);
    setReason("");
    setDeductWithdrawn(false);
    setSubmitError(null);
    getRefundPreview(order.order_id, idToken)
      .then((res) => setPreview(res.data))
      .catch((err) => setLoadError(errorText(err, "Couldn't load what this refund would change.")));
  }, [order, idToken]);

  if (!order) return null;
  const withdrawn = preview?.earnings.filter((e) => e.withdrawn) ?? [];
  const removable = preview?.earnings.filter((e) => !e.withdrawn) ?? [];
  const itemName = (id: string) => order.items.find((i) => i.order_item_id === id)?.name ?? "Unnamed listing";
  const canSubmit =
    !!preview && !preview.already_refunded && !!reason.trim() && (withdrawn.length === 0 || deductWithdrawn) && !busy;

  const submit = async () => {
    if (!idToken) return;
    setBusy(true);
    setSubmitError(null);
    try {
      const { data } = await refundOrder(order.order_id, { reason: reason.trim(), deduct_withdrawn: deductWithdrawn }, idToken);
      onRefunded(data);
    } catch (err) {
      setSubmitError(errorText(err, "Couldn't cancel this order."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onClose={busy ? undefined : onClose} maxWidth="sm" fullWidth fullScreen={narrow}>
      <DialogTitle>Cancel and refund this order?</DialogTitle>
      <DialogContent dividers sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
        <Typography variant="body2">
          Order <b className="mono">{order.order_id.slice(0, 8)}</b>
          {order.buyer ? (
            <>
              {" "}
              from <b>{order.buyer}</b>
            </>
          ) : null}
          . Every item is marked <b>Refunded</b> and the sellers' earnings for it are removed. This can't be undone from
          here.
        </Typography>

        {loadError && <ErrorState message={loadError} />}
        {!preview && !loadError && <LoadingRows rows={3} height={32} />}

        {preview?.already_refunded && <Banner>This order is already refunded.</Banner>}

        {preview && !preview.already_refunded && (
          <>
            <Box>
              <Typography variant="overline" component="div" sx={{ color: C.muted }}>
                Items to refund ({preview.items.length})
              </Typography>
              <Box component="ul" sx={{ mt: 0.5, mb: 0, pl: 2.5 }}>
                {preview.items.map((i) => (
                  <li key={i.order_item_id}>
                    <Typography variant="body2">
                      {itemName(i.order_item_id)} × {i.quantity} ·{" "}
                      {formatMoney((i.price + i.delivery_fee) * i.quantity, i.currency)}{" "}
                      <Typography component="span" variant="caption" sx={{ color: C.muted }}>
                        (now {i.status})
                      </Typography>
                    </Typography>
                  </li>
                ))}
              </Box>
            </Box>

            <Box>
              <Typography variant="overline" component="div" sx={{ color: C.muted }}>
                Seller earnings
              </Typography>
              {preview.earnings.length === 0 ? (
                <Typography variant="body2" sx={{ color: C.muted }}>
                  No earnings recorded for this order.
                </Typography>
              ) : (
                <Box component="ul" sx={{ mt: 0.5, mb: 0, pl: 2.5 }}>
                  {removable.map((e) => (
                    <li key={e.seller_id}>
                      <Typography variant="body2">
                        Remove {formatMoney(e.amount, e.currency)} from <b>{e.seller}</b>
                        <Typography component="span" variant="caption" sx={{ color: C.muted }}>
                          {" "}
                          (not withdrawn yet)
                        </Typography>
                      </Typography>
                    </li>
                  ))}
                  {withdrawn.map((e) => (
                    <li key={e.seller_id}>
                      <Typography variant="body2" sx={{ color: C.coralText }}>
                        <b>{e.seller}</b> already withdrew {formatMoney(e.amount, e.currency)} for this order
                      </Typography>
                    </li>
                  ))}
                </Box>
              )}
              {withdrawn.length > 0 && (
                <FormControlLabel
                  sx={{ mt: 0.5, alignItems: "flex-start" }}
                  control={
                    <Checkbox checked={deductWithdrawn} onChange={(e) => setDeductWithdrawn(e.target.checked)} sx={{ pt: 0.25 }} />
                  }
                  label={
                    <Typography variant="body2">
                      Deduct it from {withdrawn.length === 1 ? "their" : "each seller's"} next payout. Their balance goes
                      negative until new sales cover it; nothing is paid out while it's below zero.
                    </Typography>
                  }
                />
              )}
            </Box>

            <TextField
              label="Reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              required
              multiline
              minRows={2}
              inputProps={{ maxLength: 500 }}
              helperText="Recorded in the activity log"
            />

            <Banner
              tone="warning"
              title={
                preview.stripe_refund
                  ? `Refund ${preview.stripe_refund.currency} ${preview.stripe_refund.amount.toFixed(2)} in Stripe too.`
                  : "Refund in Stripe too."
              }
            >
              This doesn't send money back to the buyer.
              {preview.stripe_refund && preview.stripe_refund.discount > 0
                ? ` That's what their card was charged for these items, after ${preview.stripe_refund.currency} ${preview.stripe_refund.discount.toFixed(2)} of vouchers or discount codes.`
                : ""}
            </Banner>
          </>
        )}

        {submitError && <Alert severity="error">{submitError}</Alert>}
      </DialogContent>
      <DialogActions sx={{ pb: narrow ? "calc(12px + env(safe-area-inset-bottom, 0px))" : undefined, "& > *": { flex: { xs: 1, sm: "0 0 auto" } } }}>
        <Button variant="outlined" onClick={onClose} disabled={busy}>
          Keep order
        </Button>
        <Button
          variant="contained"
          color="secondary"
          onClick={submit}
          disabled={!canSubmit}
        >
          {busy ? "Working…" : "Cancel and refund"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
